import React, { useState, useEffect, useRef } from 'react';
import { EditorContent } from '@tiptap/react';
import { Eye, Code, Type } from 'lucide-react';
import { useEmailEditor } from './useEmailEditor';
import EmailEditorToolbar from './EmailEditorToolbar';
import EmailVariableButtons from './EmailVariableButtons';
import EmailPreviewPanel from './EmailPreviewPanel';

type Tab = 'visual' | 'source' | 'preview';

interface EmailRichTextEditorProps {
  contentKey: string;
  bodyHtml: string;
  subject: string;
  variables: string[];
  onBodyHtmlChange: (html: string) => void;
}

const EmailRichTextEditor: React.FC<EmailRichTextEditorProps> = ({
  contentKey,
  bodyHtml,
  subject,
  variables,
  onBodyHtmlChange,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('visual');
  const [sourceCode, setSourceCode] = useState(bodyHtml);
  const prevKeyRef = useRef(contentKey);

  const editor = useEmailEditor({
    content: bodyHtml,
    onUpdate: (html) => {
      onBodyHtmlChange(html);
      setSourceCode(html);
    },
  });

  // Reload editor content when template switches (contentKey changes)
  useEffect(() => {
    if (editor && contentKey !== prevKeyRef.current) {
      editor.commands.setContent(bodyHtml);
      setSourceCode(bodyHtml);
      setActiveTab('visual');
      prevKeyRef.current = contentKey;
    }
  }, [contentKey, bodyHtml, editor]);

  // Sync source code back to editor when switching from source tab
  const handleTabChange = (tab: Tab) => {
    if (activeTab === 'source' && tab !== 'source' && editor) {
      editor.commands.setContent(sourceCode);
      onBodyHtmlChange(sourceCode);
    }
    setActiveTab(tab);
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'visual', label: 'Visual', icon: <Type size={14} /> },
    { id: 'source', label: 'Source', icon: <Code size={14} /> },
    { id: 'preview', label: 'Preview', icon: <Eye size={14} /> },
  ];

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 bg-white">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Visual editor */}
      {activeTab === 'visual' && editor && (
        <>
          <EmailEditorToolbar editor={editor} />
          <EmailVariableButtons editor={editor} variables={variables} />
          <EditorContent
            editor={editor}
            className="min-h-[300px] max-h-[500px] overflow-y-auto px-4 py-3
              [&_.tiptap]:outline-none [&_.tiptap]:min-h-[280px]
              [&_.tiptap_h1]:text-2xl [&_.tiptap_h1]:font-bold [&_.tiptap_h1]:mb-3
              [&_.tiptap_h2]:text-xl [&_.tiptap_h2]:font-semibold [&_.tiptap_h2]:mb-2
              [&_.tiptap_h3]:text-lg [&_.tiptap_h3]:font-medium [&_.tiptap_h3]:mb-2
              [&_.tiptap_p]:mb-2
              [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-6 [&_.tiptap_ul]:mb-2
              [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-6 [&_.tiptap_ol]:mb-2
              [&_.tiptap_a]:text-indigo-600 [&_.tiptap_a]:underline
              [&_.tiptap_.is-editor-empty:first-child::before]:text-gray-400
              [&_.tiptap_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
              [&_.tiptap_.is-editor-empty:first-child::before]:float-left
              [&_.tiptap_.is-editor-empty:first-child::before]:h-0
              [&_.tiptap_.is-editor-empty:first-child::before]:pointer-events-none"
          />
        </>
      )}

      {/* Source code editor */}
      {activeTab === 'source' && (
        <div className="p-0">
          <textarea
            value={sourceCode}
            onChange={e => {
              setSourceCode(e.target.value);
              onBodyHtmlChange(e.target.value);
            }}
            className="w-full min-h-[400px] px-4 py-3 font-mono text-sm border-0 focus:ring-0 focus:outline-none resize-y"
            spellCheck={false}
          />
        </div>
      )}

      {/* Preview */}
      {activeTab === 'preview' && (
        <EmailPreviewPanel bodyHtml={bodyHtml} subject={subject} variables={variables} />
      )}
    </div>
  );
};

export default EmailRichTextEditor;
